import { nextTick } from 'vue'

export default defineNuxtPlugin((nuxtApp) => {
  nuxtApp.vueApp.directive('resizeVerticaly', {
    mounted(el: HTMLElement, binding) {
      if ((el as any).__rvInit) return // prevent double init
      ;(el as any).__rvInit = true

      // Function to retry getting bounding rects
      const retry = (attempts = 3) => {
        const dragger = el.getElementsByClassName('resize-dragger')[0] as HTMLElement | undefined
        const top = el.getElementsByClassName('resize-top')[0] as HTMLElement | undefined
        const bottom = el.getElementsByClassName('resize-bottom')[0] as HTMLElement | undefined
        const container = el as HTMLElement

        // Check if elements are valid and connected
        if (!dragger || !top || !bottom || !el.isConnected) return

        const draggerRect = dragger.getBoundingClientRect()
        const topRect = top.getBoundingClientRect()
        const bottomRect = bottom.getBoundingClientRect()

        // If any of the bounding rects are zero, retry
        if (
          (draggerRect.width === 0 || draggerRect.height === 0) &&
          (topRect.width === 0 || topRect.height === 0) &&
          (bottomRect.width === 0 || bottomRect.height === 0) &&
          attempts > 0
        ) {
          console.log('Retrying resizing logic...')

          // Retry after a short delay
          setTimeout(() => retry(attempts - 1), 500) // Retry with a 100ms delay
          return
        }

        // Proceed with the resizing setup if valid rects are obtained
        console.log('Bounding Client Rects:', draggerRect, topRect, bottomRect)

        // Initialize styles and logic for resizing
        dragger.style.position = 'absolute'
        dragger.style.bottom = bottomRect.height + 'px'

        top.style.position = 'absolute'
        top.style.left = '10px'
        top.style.right = '10px'
        top.style.top = '0px'

        top.style.bottom = draggerRect.height + bottomRect.height + 'px'

        bottom.style.position = 'absolute'
        bottom.style.left = '10px'
        bottom.style.right = '10px'
        bottom.style.bottom = '0px'
        let lastDownY = 0

        const updatePosition = (clientY?: number) => {
          let offsetBottom = dragger.getBoundingClientRect().height + bottom.getBoundingClientRect().height
          if (clientY !== undefined) {
            offsetBottom = container.getBoundingClientRect().height - (clientY - container.getBoundingClientRect().top) + 10
          }

          // Limit height if there are many events
          if (binding?.value?.eventsCount > 2 && offsetBottom > 190) {
            offsetBottom = 190
          }
          top.style.top = '0px'
          top.style.bottom = offsetBottom + 'px'

          dragger.style.top = top.getBoundingClientRect().height + 'px'

          bottom.style.top = top.getBoundingClientRect().height + dragger.getBoundingClientRect().height + 'px'
          bottom.style.bottom = '0px'
        }

        // --- Initial position when component mounts
        updatePosition()

        // Attach move/up events only while dragging
        const onMouseMove = (e: MouseEvent) => updatePosition(e.clientY)

        const onMouseUp = () => {
          window.removeEventListener('mousemove', onMouseMove)
          window.removeEventListener('mouseup', onMouseUp, true)
        }

        const onMouseDown = (e: MouseEvent) => {
          lastDownY = e.clientY
          window.addEventListener('mousemove', onMouseMove)
          window.addEventListener('mouseup', onMouseUp, true)
        }

        dragger.addEventListener('mousedown', onMouseDown)

        // Keep references for cleanup
        ;(el as any).__rvCleanup = () => {
          dragger.removeEventListener('mousedown', onMouseDown)
          window.removeEventListener('mousemove', onMouseMove)
          window.removeEventListener('mouseup', onMouseUp, true)
        }
      }

      nextTick(() => {
        retry() // Start the retry logic
      })
    },

    unmounted(el: HTMLElement) {
      const rafId = (el as any).__rvRafId as number | undefined
      if (rafId) cancelAnimationFrame(rafId) // Cancel deferred setup

      const cleanup = (el as any).__rvCleanup as (() => void) | undefined
      if (cleanup) cleanup()

      delete (el as any).__rvCleanup
      delete (el as any).__rvRafId
      delete (el as any).__rvInit
    },
  })
})
